/**
 * Behaviour for the Jessie Premium featured pages (WP3).
 *
 * Seven sections share this one file because they share one idea: Liquid
 * renders an empty, labelled shell and this script fills it from
 * window.MCM_COLOURS through window.JessieColour. Liquid cannot read a
 * JavaScript global, so nothing here is "progressive enhancement" — a shell
 * that is never filled stays hidden, which is the whole point (build plan
 * §1.2, §1.6 rule 2).
 *
 *   hero        the live colour picker over a tintable print
 *   palette     the colour grid, the family chips and the collapse
 *   band        the Colour Match band's copy; the matching itself is
 *               assets/jessie-match.js, shared with the PDP callout
 *   spools      colour meta on the featured product cards
 *   chooser     "Which Jessie?" — answers to one outcome
 *   tabs        the printer chips and the four big print-settings values
 *   crosslinks  the colour strips and counts on the other-lines cards
 *
 * The file may be emitted more than once (every section asks for the script it
 * needs and a repeated src re-executes), so it opens with the guard of §1.6
 * rule 7 and every init() marks what it has already wired with
 * data-jessie-filled.
 *
 * The chooser's rule semantics are the ones written down and tested in
 * shared/jessie-chooser.js: every `key=value` line on an outcome must hold, an
 * outcome with no rules is the fallback, block order decides ties, comparison
 * is trimmed and case-insensitive, and an unanswered question fails every rule
 * that names it. That file is the specification; this is its browser mirror.
 */
(function () {
  'use strict';

  if (window.__jessie_featured) return;
  window.__jessie_featured = true;

  var FILLED = 'data-jessie-filled';

  function jc() {
    return window.JessieColour;
  }

  function marked(el) {
    if (!el || el.hasAttribute(FILLED)) return true;
    el.setAttribute(FILLED, '');
    return false;
  }

  function all(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function text(el, value) {
    if (el) el.textContent = value == null ? '' : String(value);
  }

  /** Every [data-jessie-fill] under `root` gets its template filled (§3.4). */
  function applyFills(root, vars) {
    all(root, '[data-jessie-fill]').forEach(function (el) {
      text(el, jc().fill(el.getAttribute('data-jessie-fill'), vars));
    });
  }

  function num(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function on(el, type, fn) {
    if (el) el.addEventListener(type, fn);
  }

  /* ------------------------------------------------------------ colour meta */

  /**
   * The reference line under a colour: "#0081AF · PMS P 116-15 C · RAL 640-2",
   * with each part switched off by a setting and a missing RAL replaced by the
   * section's own "no close match" wording rather than by nothing (§3.2).
   */
  function refLine(row, opts) {
    var parts = [];
    if (opts.hex !== false && row.hex) parts.push(row.hex);
    if (opts.pms !== false && row.pantone) parts.push('PMS ' + row.pantone);
    if (opts.ral !== false) parts.push(row.ral ? row.ral : opts.noRal || '');
    return parts.filter(Boolean).join(' · ');
  }

  /** Clone the tile shell the section rendered once, and paint it. */
  function tileFrom(proto, row) {
    if (!proto || !proto.content) return null;
    var source = proto.content.firstElementChild;
    if (!source) return null;
    var tile = source.cloneNode(true);
    tile.setAttribute('data-jessie-tile', row.id);
    if (!jc().paintTile(tile, row)) return null;
    return tile;
  }

  /* ------------------------------------------------------------------- hero */

  function initHero(root) {
    if (marked(root)) return;
    var picker = root.querySelector('[data-jessie-picker]');
    if (!picker) return;

    var lineName = root.getAttribute('data-jessie-line');
    /* Hue-ordered, neutrals last: a strip of dots in table (alphabetical)
       order reads as random. The spectrum strip below inherits the order. */
    var rows = jc().displayOrder(jc().line(lineName));
    if (!rows.length) return;

    /* "See all {n} colors" and anything else the copy counts. */
    applyFills(root, { line: lineName, line_label: jc().lineLabel(lineName), n: rows.length });

    var swatches = picker.querySelector('[data-jessie-swatches]');
    var target = picker.querySelector('[data-jessie-pick-target]');
    var nameEl = picker.querySelector('[data-jessie-pick-name]');
    var refsEl = picker.querySelector('[data-jessie-pick-refs]');
    var link = picker.querySelector('[data-jessie-pick-link]');
    var linkLabel = picker.getAttribute('data-jessie-link-label') || '';
    var opts = {
      hex: picker.getAttribute('data-jessie-show-hex') !== 'false',
      pms: picker.getAttribute('data-jessie-show-pms') !== 'false',
      ral: picker.getAttribute('data-jessie-show-ral') !== 'false',
      noRal: picker.getAttribute('data-jessie-no-ral') || '',
    };

    var buttons = [];

    function select(index) {
      var row = rows[index];
      if (!row) return;
      buttons.forEach(function (button, i) {
        button.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });
      if (target) target.style.setProperty('--jessie-pick', row.hex);
      text(nameEl, row.name);
      text(refsEl, refLine(row, opts));
      if (link) {
        link.href = row.url || '#';
        text(link.querySelector('[data-jessie-pick-link-label]'), (linkLabel + ' ' + row.name).trim());
        link.hidden = !row.url;
      }
    }

    rows.forEach(function (row, i) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'jessie-dot';
      button.style.setProperty('--jessie-dot', row.hex);
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = '<span class="visually-hidden"></span>';
      text(button.firstChild, row.name);
      button.addEventListener('click', function () {
        select(i);
      });
      buttons.push(button);
      if (swatches) swatches.appendChild(button);
    });

    var wanted = (picker.getAttribute('data-jessie-default-id') || '').trim();
    var start = 0;
    if (wanted) {
      rows.forEach(function (row, i) {
        if (row.id === wanted) start = i;
      });
    }
    select(start);

    var spectrum = root.querySelector('[data-jessie-spectrum]');
    if (spectrum) {
      rows.forEach(function (row) {
        var band = document.createElement('span');
        band.className = 'jessie-strip__band';
        band.style.setProperty('--jessie-band', row.hex);
        spectrum.appendChild(band);
      });
      spectrum.hidden = false;
    }

    picker.hidden = false;
  }

  /* ---------------------------------------------------------------- palette */

  function paletteCell(row, proto, opts) {
    var tile = tileFrom(proto, row);
    if (!tile) return null;

    var cell = document.createElement('div');
    cell.className = 'jessie-palette__cell';
    cell.setAttribute('data-jessie-family', jc().family(row));

    var link = document.createElement(row.url ? 'a' : 'div');
    link.className = 'jessie-palette__link';
    if (row.url) link.href = row.url;

    var name = document.createElement('span');
    name.className = 'jessie-palette__name';
    text(name, row.name);

    var meta = document.createElement('span');
    meta.className = 'jessie-palette__meta';
    text(meta, refLine(row, opts));

    link.appendChild(tile);
    link.appendChild(name);
    link.appendChild(meta);
    cell.appendChild(link);
    return cell;
  }

  function initPalette(root) {
    if (marked(root)) return;
    var grid = root.querySelector('[data-jessie-grid]');
    var proto = root.querySelector('[data-jessie-tile-proto]');
    if (!grid || !proto) return;

    var rows = jc().line(root.getAttribute('data-jessie-line'));
    if (!rows.length) return;

    var opts = {
      hex: root.getAttribute('data-jessie-show-hex') !== 'false',
      pms: root.getAttribute('data-jessie-show-pms') !== 'false',
      ral: root.getAttribute('data-jessie-show-ral') !== 'false',
      noRal: root.getAttribute('data-jessie-no-ral') || '',
    };

    /* The bundle cards are Liquid's, hidden, each carrying the tile index it
       belongs after. Take them out, build the tiles, put them back. */
    var bundles = all(grid, '[data-jessie-insert-after]');
    bundles.forEach(function (card) {
      if (card.parentNode) card.parentNode.removeChild(card);
    });

    var cells = [];
    rows.forEach(function (row) {
      var cell = paletteCell(row, proto, opts);
      if (cell) cells.push(cell);
    });
    if (!cells.length) return;

    var fragment = document.createDocumentFragment();
    var placed = [];
    cells.forEach(function (cell, i) {
      bundles.forEach(function (card) {
        if (placed.indexOf(card) === -1 && num(card.getAttribute('data-jessie-insert-after'), 0) === i) {
          fragment.appendChild(card);
          placed.push(card);
        }
      });
      fragment.appendChild(cell);
    });
    bundles.forEach(function (card) {
      if (placed.indexOf(card) === -1) fragment.appendChild(card);
    });
    bundles.forEach(function (card) {
      all(card, '[data-jessie-band-handle]').forEach(function (band) {
        var member = jc().byHandle(band.getAttribute('data-jessie-band-handle'));
        if (member && member.hex) {
          band.style.setProperty('--jessie-band', member.hex);
          band.hidden = false;
        }
      });
      card.hidden = false;
    });
    grid.appendChild(fragment);

    applyFills(root, { line: root.getAttribute('data-jessie-line'), line_label: jc().lineLabel(root.getAttribute('data-jessie-line')), n: cells.length });

    wireCollapse(root, grid, cells.length + bundles.length);
    wireChips(root, grid, cells, bundles);
    root.hidden = false;
  }

  function wireChips(root, grid, cells, bundles) {
    var chipBox = root.querySelector('[data-jessie-chips]');
    if (!chipBox) return;
    var status = root.querySelector('[data-jessie-status]');
    var counts = {};
    cells.forEach(function (cell) {
      var family = cell.getAttribute('data-jessie-family');
      counts[family] = (counts[family] || 0) + 1;
    });

    var chips = [];

    function apply(family) {
      var shown = 0;
      cells.forEach(function (cell) {
        var match = family === '' || cell.getAttribute('data-jessie-family') === family;
        cell.hidden = !match;
        if (match) shown += 1;
      });
      bundles.forEach(function (card) {
        card.hidden = family !== '';
      });
      chips.forEach(function (chip) {
        chip.setAttribute('aria-pressed', chip.getAttribute('data-jessie-family') === family ? 'true' : 'false');
      });
      /* A filter and a collapse cap cannot both be honoured: the cap is a
         :nth-child rule and counts hidden cells. So filtering expands. */
      grid.classList.toggle('is-expanded', family !== '');
      var wrap = root.querySelector('.jessie-palette__more');
      var more = root.querySelector('[data-jessie-more]');
      if (wrap && more) wrap.hidden = family !== '' || !more.getAttribute('data-jessie-collapsible');
      text(status, shown + ' ' + (root.getAttribute('data-jessie-status-word') || 'colors'));
    }

    function chip(label, family) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'jessie-chip';
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('data-jessie-family', family);
      text(button, label);
      button.addEventListener('click', function () {
        apply(family);
      });
      chips.push(button);
      chipBox.appendChild(button);
    }

    chip((root.getAttribute('data-jessie-all-label') || 'All') + ' · ' + cells.length, '');
    jc().FAMILIES.forEach(function (family) {
      if (counts[family]) chip(family + ' · ' + counts[family], family);
    });
    apply('');
    chipBox.hidden = false;
  }

  function wireCollapse(root, grid, total) {
    var wrap = root.querySelector('.jessie-palette__more');
    var button = root.querySelector('[data-jessie-more]');
    if (!wrap || !button) return;
    var cap = num(root.getAttribute('data-jessie-cap'), 0);
    if (cap <= 0 || total <= cap) return;
    button.setAttribute('data-jessie-collapsible', 'true');
    wrap.hidden = false;
    button.addEventListener('click', function () {
      var expanded = grid.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      text(
        button.querySelector('[data-jessie-more-label]'),
        expanded ? button.getAttribute('data-less-label') : button.getAttribute('data-more-label')
      );
    });
  }

  /* --------------------------------------------------------- colour match band */

  /**
   * The band's own job is now only its copy: the ranked matches, the input,
   * the colour picker and the gate-aware rendering are assets/jessie-match.js,
   * which the product page's Color Match callout mounts the same way. Parsing
   * and ΔE2000 live in exactly one file (owner feedback, 2026-09-05).
   */
  function initBand(root) {
    if (marked(root)) return;
    var line = root.getAttribute('data-jessie-line');
    var rows = jc().line(line);

    applyFills(root, { line: line, line_label: jc().lineLabel(line), n: rows.length });

    if (!window.JessieMatch) return;
    /* mount() returns null for a root the safety-net init() already wired; in
       that case repaint, because that pass may have run before the colour
       table was there. */
    if (!window.JessieMatch.mount(root)) window.JessieMatch.render(root);
  }

  /* ----------------------------------------------------------------- spools */

  function initSpools(root) {
    if (marked(root)) return;
    var proto = root.querySelector('[data-jessie-tile-proto]');
    /* "Color rows only" (section setting `colour_only`, default on). In
       collection mode the source is a line collection, and those are not
       colour-only: `jessie-elixir` carries "Jessie Premium Elixir™ 1.75mm X
       Transition roll 1kg", which is in no colour table. Such a card had a
       placeholder image, the raw title and no meta line — the one broken cell in
       an otherwise uniform row. It is dropped instead. */
    var colourOnly = root.getAttribute('data-jessie-colour-only') !== 'false';
    var kept = 0;
    var dropped = 0;
    all(root, '[data-jessie-handle]').forEach(function (card) {
      /* byHandle, not resolve: the meta line claims 1.75 mm and 1 kg, which is
         true of the MCM row's own product and of no other weight's handle. */
      var row = jc().byHandle(card.getAttribute('data-jessie-handle'));
      if (!row) {
        if (colourOnly) {
          card.hidden = true;
          dropped += 1;
        }
        return;
      }
      kept += 1;
      var opts = {
        hex: root.getAttribute('data-jessie-show-hex') !== 'false',
        pms: root.getAttribute('data-jessie-show-pms') !== 'false',
        ral: root.getAttribute('data-jessie-show-ral') !== 'false',
        noRal: root.getAttribute('data-jessie-no-ral') || '',
      };
      /* The label comes from the ROW, not from JessieColour.lineLabel(row.line):
         lineLabel resolves a *Jessie* line name, so a Prusament PETG row — which
         collection mode does return, the switching collection of a Prusament PDP
         being a Prusament collection — would be labelled "Jessie Premium PETG".
         MCM already carries the honest per-row label ("Prusament PETG"), so
         non-Jessie rows use it and only Jessie rows go through the ™ setting. */
      var rowLabel =
        row.brand === 'Jessie' ? jc().lineLabel(row.line) : row.line_label || row.line || '';
      applyFills(card, { line: row.line, line_label: rowLabel });
      text(card.querySelector('[data-jessie-spool-name]'), row.name);
      text(card.querySelector('[data-jessie-spool-refs]'), refLine(row, opts));
      var dot = card.querySelector('[data-jessie-spool-dot]');
      if (dot) {
        dot.style.setProperty('--jessie-dot', row.hex);
        dot.hidden = false;
      }
      var meta = card.querySelector('[data-jessie-spool-meta]');
      if (meta) meta.hidden = false;
      /* No product image: the colour's own tile stands in for the photo. */
      var slot = card.querySelector('[data-jessie-spool-tile-slot]');
      if (slot && !slot.firstElementChild) {
        var tile = tileFrom(proto, row);
        if (tile) slot.appendChild(tile);
      }
    });
    /* Every card dropped: a kicker and a heading over an empty grid is the empty
       box the build forbids, so the whole row goes. */
    if (colourOnly && dropped && !kept) root.hidden = true;
  }

  /* ---------------------------------------------------------------- chooser */

  function norm(value) {
    return String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function parseRules(source) {
    return String(source == null ? '' : source)
      .split('\n')
      .map(function (line) {
        return line.trim();
      })
      .filter(function (line) {
        return line && line.charAt(0) !== '#' && line.indexOf('=') !== -1;
      })
      .map(function (line) {
        var at = line.indexOf('=');
        return { key: norm(line.slice(0, at)), value: norm(line.slice(at + 1)) };
      })
      .filter(function (rule) {
        return rule.key;
      });
  }

  function outcomeMatches(el, answers) {
    var rules = parseRules(el.getAttribute('data-jessie-rules'));
    if (!rules.length) return true;
    return rules.every(function (rule) {
      return answers[rule.key] !== undefined && answers[rule.key] === rule.value;
    });
  }

  function initChooser(root) {
    if (marked(root)) return;
    var questions = all(root, '[data-jessie-question]');
    var outcomes = all(root, '[data-jessie-outcome]');
    var pending = root.querySelector('[data-jessie-chooser-pending]');
    var answers = {};

    function show() {
      var complete = questions.every(function (question) {
        return answers[norm(question.getAttribute('data-jessie-question'))] !== undefined;
      });
      var chosen = null;
      if (complete && questions.length) {
        for (var i = 0; i < outcomes.length; i++) {
          if (outcomeMatches(outcomes[i], answers)) {
            chosen = outcomes[i];
            break;
          }
        }
      }
      outcomes.forEach(function (outcome) {
        outcome.hidden = outcome !== chosen;
      });
      if (pending) pending.hidden = !!chosen;
      if (chosen) paintOutcome(chosen);
    }

    function paintOutcome(outcome) {
      if (outcome.hasAttribute('data-jessie-painted')) return;
      outcome.setAttribute('data-jessie-painted', '');
      var names = [];
      all(outcome, '[data-jessie-swatch]').forEach(function (swatch) {
        var row = jc().byId(swatch.getAttribute('data-jessie-swatch'));
        if (!row) return;
        swatch.style.setProperty('--swatch-background', row.hex);
        swatch.hidden = false;
        names.push(row.name);
      });
      var namesEl = outcome.querySelector('[data-jessie-outcome-names]');
      if (namesEl) {
        if (names.length) {
          text(namesEl, ((namesEl.getAttribute('data-jessie-start-label') || '') + ' ' + names.join(', ')).trim());
          namesEl.hidden = false;
        } else {
          namesEl.hidden = true;
        }
      }
    }

    questions.forEach(function (question) {
      var key = norm(question.getAttribute('data-jessie-question'));
      var options = all(question, '[data-jessie-option]');
      options.forEach(function (option) {
        option.addEventListener('click', function () {
          answers[key] = norm(option.getAttribute('data-jessie-option'));
          options.forEach(function (other) {
            other.setAttribute('aria-pressed', other === option ? 'true' : 'false');
          });
          show();
        });
      });
    });

    all(root, '[data-jessie-reset]').forEach(function (reset) {
      reset.addEventListener('click', function () {
        answers = {};
        all(root, '[data-jessie-option]').forEach(function (option) {
          option.setAttribute('aria-pressed', 'false');
        });
        show();
      });
    });

    show();
  }

  /* ------------------------------------------------------------------- tabs */

  function initTabs(root) {
    if (marked(root)) return;
    if (!jc().profiles) return;
    var line = root.getAttribute('data-jessie-line');
    var data = jc().profiles.forLine(line);
    if (!data || !data.rows || !data.rows.length) return;

    var chipBox = root.querySelector('[data-jessie-tab-chips]');
    var chips = [];

    function paint(index) {
      var profile = data.rows[index];
      if (!profile) return;
      chips.forEach(function (chip, i) {
        chip.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });
      ['nozzle', 'first', 'bed', 'fan'].forEach(function (key) {
        text(root.querySelector('[data-jessie-value="' + key + '"]'), profile[key] || '');
      });
      var profileEl = root.querySelector('[data-jessie-profile]');
      if (profileEl) {
        text(profileEl, profile.profile || '');
        var profileRow = profileEl.closest('[data-jessie-profile-row]');
        if (profileRow) profileRow.hidden = !profile.profile;
      }
      var flowEl = root.querySelector('[data-jessie-flow]');
      if (flowEl) {
        text(flowEl, profile.vol || '');
        var flowRow = flowEl.closest('[data-jessie-flow-row]');
        if (flowRow) flowRow.hidden = !profile.vol;
      }
      var hfEl = root.querySelector('[data-jessie-hf]');
      if (hfEl) {
        text(hfEl, profile.hf || '');
        hfEl.hidden = !profile.hf;
      }
    }

    data.rows.forEach(function (profile, i) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'jessie-chip jessie-chip--on-accent';
      chip.setAttribute('aria-pressed', 'false');
      text(chip, profile.label || '');
      chip.addEventListener('click', function () {
        paint(i);
      });
      chips.push(chip);
      if (chipBox) chipBox.appendChild(chip);
    });
    paint(0);

    var note = root.querySelector('[data-jessie-tabs-note]');
    if (note && note.textContent.trim() === '' && data.note) {
      text(note, data.note);
      note.hidden = false;
    }
    var source = root.querySelector('[data-jessie-tabs-source]');
    if (source && data.source) {
      text(source, ((source.getAttribute('data-jessie-source-prefix') || '') + ' ' + data.source).trim());
      source.hidden = false;
    }
    root.hidden = false;
  }

  /* ------------------------------------------------------------- crosslinks */

  /** `count` rows spaced evenly through `rows`; all of them if there are fewer. */
  function spread(rows, count) {
    if (count <= 0 || rows.length <= count) return rows;
    var out = [];
    for (var i = 0; i < count; i++) out.push(rows[Math.floor((i * rows.length) / count)]);
    return out;
  }

  function initCrosslinks(root) {
    if (marked(root)) return;
    all(root, '[data-jessie-crosslink]').forEach(function (card) {
      var line = card.getAttribute('data-jessie-crosslink');
      var rows = jc().line(line);
      if (!rows.length) return;
      applyFills(card, { line: line, line_label: jc().lineLabel(line), n: rows.length });
      var strip = card.querySelector('[data-jessie-strip]');
      if (strip) {
        var count = num(strip.getAttribute('data-jessie-strip-count'), 12);
        /* Hue order, sampled evenly: twelve of thirty-six should span the
           whole line, not be its first twelve names. */
        spread(jc().displayOrder(rows), count).forEach(function (row) {
          var band = document.createElement('span');
          band.className = 'jessie-strip__band';
          band.style.setProperty('--jessie-band', row.hex);
          strip.appendChild(band);
        });
        strip.hidden = false;
      }
      all(card, '[data-jessie-count]').forEach(function (el) {
        el.hidden = false;
      });
    });
  }

  /* ------------------------------------------------------------------- boot */

  function withColours(fn) {
    return function (el) {
      if (!jc()) return;
      jc().ready(function () {
        fn(el);
      });
    };
  }

  function init() {
    /* Profiles, not colours: the printer tabs need no colour table. */
    if (jc()) all(document, '[data-jessie-tabs]').forEach(initTabs);
    var map = [
      ['[data-jessie-hero]', initHero],
      ['[data-jessie-palette]', initPalette],
      ['[data-jessie-band]', initBand],
      ['[data-jessie-spools]', initSpools],
      ['[data-jessie-chooser]', initChooser],
      ['[data-jessie-crosslinks]', initCrosslinks],
    ];
    map.forEach(function (entry) {
      all(document, entry[0]).forEach(withColours(entry[1]));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('product:section-swapped', init);
})();
